import { useEffect, useState } from 'react';
import { subscribeToHostTutorialState } from '../utils/iosTutorialBridge';
import { subscribeToPanelDemoVisible } from '../utils/panelDemoVisibility';

/**
 * Whether the iOS host is telling the user to close this tool window.
 *
 * A tutorial lesson that has opened one of these panels stands on a step hosted
 * by the panel itself, and its line there ends "close this when you're done".
 * The host cannot point at the ✕: its coach marks are native views drawn over
 * the WebView, and the ✕ is a DOM node inside it. So the host sends a flag and
 * the web draws the attention — which is what `Modal`'s `pulseClose` was built
 * for in the first place.
 *
 * @param {boolean} enabled only subscribe while this window is actually open
 * @returns {boolean}
 */
export default function useHostCloseHint(enabled) {
  const [hinted, setHinted] = useState(false);
  const [demoShowing, setDemoShowing] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    return subscribeToPanelDemoVisible(setDemoShowing);
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      // Reset rather than keep the last answer: a window that reopens must not
      // start out pulsing because a previous lesson once asked it to close.
      setHinted(false);
      return undefined;
    }
    return subscribeToHostTutorialState(({ closeHint }) => {
      setHinted(!!closeHint);
    });
  }, [enabled]);

  // Teach first, then invite the close. The host asks for both the moment a
  // lesson lands in a tool, and pulsing the ✕ over a running walkthrough tells
  // the user to leave the thing they are being shown.
  return hinted && !demoShowing;
}
